import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface Notification {
  id: string;
  message: string;
  type: AlertColor;
  autoHide?: boolean;
  duration?: number;
}

interface NotificationContextType {
  showNotification: (message: string, type?: AlertColor, autoHide?: boolean, duration?: number) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  hideNotification: (id: string) => void;
  clearAll: () => void;
}

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const generateId = () => `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const showNotification = (
    message: string,
    type: AlertColor = 'info',
    autoHide: boolean = true,
    duration: number = 6000
  ) => {
    const id = generateId();
    const notification: Notification = {
      id,
      message,
      type,
      autoHide,
      duration,
    };

    setNotifications(prev => [...prev, notification]);

    if (autoHide) {
      setTimeout(() => {
        hideNotification(id);
      }, duration);
    }
  };

  const showSuccess = (message: string) => {
    showNotification(message, 'success');
  };

  const showError = (message: string) => {
    showNotification(message, 'error', true, 8000); // Keep errors longer
  };

  const showWarning = (message: string) => {
    showNotification(message, 'warning', true, 7000);
  };

  const showInfo = (message: string) => {
    showNotification(message, 'info');
  };

  const hideNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const value: NotificationContextType = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideNotification,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      
      {/* Render notifications */}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          anchorOrigin={{ 
            vertical: 'top', 
            horizontal: 'right' 
          }}
          style={{ 
            marginTop: `${index * 70}px` // Stack notifications vertically
          }}
          onClose={() => hideNotification(notification.id)}
        >
          <Alert
            onClose={() => hideNotification(notification.id)}
            severity={notification.type}
            variant="filled"
            sx={{
              width: '100%',
              minWidth: '300px',
              maxWidth: '500px',
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};
